import React, { useMemo, useState } from 'react'

import { BoxContainer, Button } from '../../../../../../../../components'
import { useInfinitePagination } from '../../../../../../../../hooks/useInfinitePagination'
import ecbrApi from '../../../../../../../../services/ecbrApi'
import { UserEvents } from '../constants'
import ErrorMessage from '../../../../../../../../components/Form/ErrorMessage'
import useUrlParam from '../../../../../../../../hooks/useUrlParam'
import {
  loadCertificateOptions,
  loadPillarOptions
} from '../../../../../../../../helpers/loadOptions'
import { useQuery } from 'react-query'
import { Content } from '../../../../../../../../components/Content'
import { ComponentTitle } from '../../../../../../styles'
import { Formik } from 'formik'
import { CheckboxContainer } from '../../../../../../../SignUp/Step1/styles'
import { errorResponseStatusHandler } from '../../../../../../../../utils/handlers/errorResponseStatusHandler'
import { Form } from '../../../../../../../../components/Form/styles'
import Radio from '../../../../../../../../components/Form/Radio'
import { objectForeachAsync } from '../../../../../../../../utils/objectForeachAsync'

const pagination = { page: 1, take: 5 }

const AssociateUserPillars = ({ eventListener }) => {
  const [brandId, certificateId] = useUrlParam(
    '/user/brands/:id/certificates/:id'
  )
  const [error, setError] = useState('')
  const [searchParameters, _setSearchParameters] = useState({
    ...pagination
  })

  const {
    data: users,
    fetchNextPage,
    hasNextPage,
    isLastPage,
    isFetching,
    isLoading,
    refetch
  } = useInfinitePagination(
    'userList',
    async (params) => {
      const { pageParam = searchParameters.page } = params
      return ecbrApi.users.getCompanyUsers({
        ...searchParameters,
        page: pageParam || searchParameters.page
      })
    },
    {
      refetchOnWindowFocus: false,
      onSuccess: () => {
        // setError('')
      },
      onError: () => {
        setError('Erro ao carregar os usuários')
      }
    }
  )

  const {
    data: certificatePillars,
    isFetching: _isFetchingPillars,
    isLoading: _isLoadingPillars,
    refetch: refetchPillars
  } = useQuery(
    'certificatePillars',
    async () => {
      const { data } =
        await ecbrApi.certificates.getAllCompanyCertificatePillars(
          certificateId
        )
      return data
    },
    {
      enabled: !!certificateId,
      cacheTime: 0,
      refetchOnWindowFocus: false,
      onSuccess: () => {
        setError('')
      },
      onError: () => {
        setError('Erro ao carregar os pilares')
      }
    }
  )

  const {
    data: _certificateOptions
    // isLoading: isLoadingCertificateOptions,
    // isFetching: isFetchingCertificateOptions
  } = useQuery(
    'certificateOptions',
    () => loadCertificateOptions(brandId)(''),
    { cacheTime: 0 }
  )

  const { data: pillarOptions } = useQuery(
    'pillarOptions',
    () => loadPillarOptions(certificateId)(''),
    { cacheTime: 0 }
  )

  const usersInitialValues = useMemo(() => {
    return pillarOptions?.reduce((acc, option) => {
      if (!option.ownerId) {
        acc[`pillar-${option.value}`] = null
      } else {
        acc[`pillar-${option.value}`] = `${option.value}-${option.ownerId}`
      }
      return acc
    }, {})
  }, [pillarOptions])

  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      setSubmitting(true)
      await objectForeachAsync(values, async (value) => {
        if (value) {
          const [pillarId, userId] = value.split('-')
          const certificatePillarId = certificatePillars.find(
            (certificatePillar) =>
              certificatePillar.pillarId === Number(pillarId)
          )?.id

          if (!certificatePillarId) {
            throw new Error('Ocorreu um erro ao associar o usuário ao pilar')
          }

          await ecbrApi.pillars.associateUserToCertificatePillar(
            Number(userId),
            certificatePillarId
          )

          setTimeout(() => {
            refetch()
            refetchPillars()
          }, 500)
          setError('')
        }
      })
    } catch (err) {
      errorResponseStatusHandler(err, setError)
    }

    eventListener?.publish(UserEvents.PILLAR_OWNER_UPDATED)
  }

  return (
    <BoxContainer>
      <ComponentTitle>Associar usuário aos pilares</ComponentTitle>

      <Content templateColumns="12% 17% 70%" gap="0" mainGap="0">
        <header>
          <ul>
            <li>Nome do usuário</li>
            <li>E-mail</li>
            <li>Pilares associados</li>
          </ul>
        </header>
        {users?.length > 0 && usersInitialValues && (
          <Formik
            initialValues={usersInitialValues}
            onSubmit={handleSubmit}
            validateOnChange={false}
          >
            {({ submitForm }) => (
              <Form align="flex-start" maxWidth="100%">
                <main>
                  {users?.map((user) => (
                    <ul key={user.id}>
                      <li>{`${user.name} ${user.lastname}`}</li>
                      <li>{user.email}</li>
                      <li>
                        {pillarOptions && (
                          <CheckboxContainer display="flex" gap="16px" wrap>
                            {pillarOptions?.map((option) => (
                              <Radio
                                key={`${option.value}-${user.id}`}
                                label={option.label}
                                name={`pillar-${option.value}`}
                                value={`${option.value}-${user.id}`}
                                width="unset"
                                onChange={() => {
                                  setTimeout(() => {
                                    submitForm()
                                  }, 500)
                                }}
                              />
                            ))}
                          </CheckboxContainer>
                        )}
                      </li>
                    </ul>
                  ))}
                  {error && <ErrorMessage>{error}</ErrorMessage>}
                  {hasNextPage && !isLastPage && (
                    <Button
                      type="button"
                      variant="outlined"
                      text={
                        isLoading || isFetching ? 'Carregando...' : 'Ver mais'
                      }
                      width="120px"
                      onClick={() => fetchNextPage()}
                    />
                  )}
                </main>
              </Form>
            )}
          </Formik>
        )}
      </Content>
    </BoxContainer>
  )
}

export default AssociateUserPillars
